import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

/**
 * Tooltip Component
 *
 * Reusable tooltip wrapper using Preline's tooltip system.
 * Uses light theme design matching Sidebar tooltips.
 * Automatically initializes Preline when mounted for dynamic content.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Element that triggers the tooltip
 * @param {React.ReactNode|string} props.content - Tooltip content (can be text or JSX)
 * @param {string} [props.placement='top'] - Tooltip placement (top/right/bottom/left)
 * @param {string} [props.trigger='hover'] - Trigger type (hover/click/focus)
 * @param {string} [props.maxWidth] - Optional max-width (e.g., 'xs', 'sm', 'md', 'lg', 'xl', '2xl')
 *
 * @example
 * <Tooltip content="Download file" placement="top">
 *   <button>Download</button>
 * </Tooltip>
 *
 * @example
 * <Tooltip content="Very long text..." placement="top" maxWidth="xs">
 *   <button>Hover me</button>
 * </Tooltip>
 */
export function Tooltip({
  children,
  content,
  placement = 'top',
  trigger = 'hover',
  maxWidth = null,
}) {
  const tooltipRef = useRef(null);

  useEffect(() => {
    // Initialize Preline for this tooltip when mounted
    if (tooltipRef.current && window.HSStaticMethods) {
      window.HSStaticMethods.autoInit(['tooltip']);
    }
  }, []);

  // Build tooltip content className based on maxWidth
  const tooltipClassName = maxWidth
    ? `hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-[100] py-2 px-3 bg-white text-zinc-800 text-sm font-medium rounded-lg shadow-lg border border-zinc-200 max-w-${maxWidth} whitespace-normal normal-case`
    : 'hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-[100] py-2 px-3 bg-white text-zinc-800 text-sm font-medium rounded-lg shadow-lg border border-zinc-200 whitespace-nowrap normal-case';

  return (
    <div
      ref={tooltipRef}
      className={`hs-tooltip [--placement:${placement}] [--trigger:${trigger}]`}
    >
      {children}
      <span
        className={tooltipClassName}
        role="tooltip"
      >
        {content}
      </span>
    </div>
  );
}

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  trigger: PropTypes.oneOf(['hover', 'click', 'focus']),
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', '6xl', '7xl']),
};
